import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  DollarSign, TrendingUp, Eye, Heart, MessageCircle, 
  Share2, Sparkles, ArrowUpRight, Loader2 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CreatorEarningsPage() {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    loadEarnings();
  }, []);

  const loadEarnings = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/creator/earnings`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setEarnings(data);
      }
    } catch (error) {
      console.error('Failed to load earnings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    try {
      setWithdrawing(true);
      const res = await fetch(`${API_URL}/api/creator/withdraw`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        alert(`Successfully withdrew ${data.amount} MTR!`);
        loadEarnings();
      } else {
        const error = await res.json();
        alert(error.detail || 'Withdrawal failed');
      }
    } catch (error) {
      alert('Withdrawal failed');
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!earnings) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-gray-600">Failed to load earnings data</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="w-8 h-8 text-yellow-500" />
            Creator Earnings
          </h1>
          <p className="text-gray-600 mt-1">
            Earn MTR tokens from your popular posts and engagement
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Earned</p>
                  <p className="text-2xl font-bold">{earnings.total_earned} MTR</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending Payout</p>
                  <p className="text-2xl font-bold text-yellow-600">{earnings.pending_payout} MTR</p>
                </div>
                <DollarSign className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Paid Out</p>
                  <p className="text-2xl font-bold text-blue-600">{earnings.paid_out} MTR</p>
                </div>
                <ArrowUpRight className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Posts</p>
                  <p className="text-2xl font-bold">{earnings.total_posts}</p>
                </div>
                <Eye className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Withdraw Button */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">Ready to withdraw?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Minimum payout: {earnings.minimum_payout} MTR
                  {!earnings.can_withdraw && ` (${earnings.pending_payout} / ${earnings.minimum_payout})`}
                </p>
              </div>
              <Button
                onClick={handleWithdraw}
                disabled={!earnings.can_withdraw || withdrawing}
                size="lg"
              >
                {withdrawing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <DollarSign className="w-4 h-4 mr-2" />
                    Withdraw {earnings.pending_payout} MTR
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Top Posts */}
        <Card>
          <CardHeader>
            <CardTitle>Top Earning Posts</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {earnings.top_posts && earnings.top_posts.length > 0 ? (
              earnings.top_posts.map((post) => (
                <div 
                  key={post.post_id}
                  className="flex items-start justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="text-sm mb-2">{post.content}</p>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {post.impressions}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-3 h-3" />
                        {post.likes}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" />
                        {post.comments}
                      </span>
                    </div>
                  </div>
                  <Badge variant="secondary" className="ml-4">
                    {post.earnings} MTR
                  </Badge>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 py-8">
                Start creating posts to earn rewards!
              </p>
            )}
          </CardContent>
        </Card>

        {/* How to Earn More */}
        <Card>
          <CardHeader>
            <CardTitle>💡 How to Earn More</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <Eye className="w-5 h-5 text-blue-500 mt-1" />
              <div>
                <p className="font-medium">Ad Impressions</p>
                <p className="text-sm text-gray-600">1 MTR per 1,000 views</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Heart className="w-5 h-5 text-red-500 mt-1" />
              <div>
                <p className="font-medium">Likes Bonus</p>
                <p className="text-sm text-gray-600">0.5 MTR per 100 likes</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MessageCircle className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <p className="font-medium">Comments Bonus</p>
                <p className="text-sm text-gray-600">0.3 MTR per 10 comments</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Share2 className="w-5 h-5 text-purple-500 mt-1" />
              <div>
                <p className="font-medium">Shares Bonus</p>
                <p className="text-sm text-gray-600">0.5 MTR per 10 shares</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
